/**
 * ForecastTimelineChart Component
 *
 * Displays forecast data over multiple days with percentile bands and threat indicators
 */

import * as React from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { getThreatIcon, getThreatColor } from "@/config/weather-threats";

// Types
export interface ForecastDataPoint {
  date: string;
  timestamp: number;
  estimate25th?: number;
  predicted_median?: number;
  estimate75th?: number;
  estimate90th?: number;
  best_estimate?: number;
  threats?: ThreatIndicator[];
  [key: string]: string | number | ThreatIndicator[] | undefined;
}

export interface ThreatIndicator {
  id: string;
  slug: string;
  name: string;
  isActive: boolean;
}

export interface ForecastTimelineChartProps {
  data: ForecastDataPoint[];
  /** Pixel height, or a percentage (e.g. `"100%"`) to fill a flex parent */
  height?: number | string;
  yAxisLabel?: string;
  className?: string;
  showGrid?: boolean;
  showTooltip?: boolean;
  showThreats?: boolean;
  showLegend?: boolean;
}

// Semantic colors for forecast lines
const LINE_COLORS = {
  estimate25th: "#93c5fd", // Light blue (low severity)
  predicted_median: "#3b82f6", // Primary blue (medium)
  estimate75th: "#1d4ed8", // Dark blue (high)
  estimate90th: "#6d28d9", // Violet (very high - distinct hue from 75th)
  best_estimate: "#1e3a8a", // Darkest blue (best estimate - dashed)
};

// Format date for display
function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    date.setUTCHours(12); // Prevents date shifting
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateString;
  }
}

// Format numbers for axis
function formatCompactNumber(value: number): string {
  const absValue = Math.abs(value);
  if (absValue >= 1000000) {
    return (value / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  } else if (absValue >= 1000) {
    return (value / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  } else if (absValue >= 1) {
    return value.toFixed(0);
  }
  return value.toFixed(2);
}

// Custom tooltip component
function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload || !payload.length) return null;

  const dataPoint = payload[0]?.payload;
  const threats =
    dataPoint?.threats?.filter((t: ThreatIndicator) => t.isActive) || [];

  return (
    <div className="p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-w-xs">
      <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
        {label}
      </p>
      {payload.map((entry: any, index: number) => (
        <div
          key={index}
          className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 mb-1"
        >
          <span
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="font-medium">{entry.name}:</span>
          <span>{formatCompactNumber(entry.value)}</span>
        </div>
      ))}

      {threats.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Weather Threats:
          </p>
          <div className="flex flex-wrap gap-1">
            {threats.map((threat: ThreatIndicator) => {
              const Icon = getThreatIcon(threat.slug) || AlertTriangle;
              const color = getThreatColor(threat.slug) || "#6b7280";
              return (
                <div
                  key={threat.id}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-xl text-xs text-white font-medium"
                  style={{ backgroundColor: color }}
                >
                  <Icon className="h-3 w-3" />
                  <span className="truncate max-w-[6rem]">{threat.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const LEGEND_ITEMS: Array<{
  key: keyof typeof LINE_COLORS;
  label: string;
  dashed?: boolean;
}> = [
  { key: "estimate25th", label: "25th Percentile" },
  { key: "predicted_median", label: "Median" },
  { key: "estimate75th", label: "75th Percentile" },
  { key: "estimate90th", label: "90th Percentile" },
  { key: "best_estimate", label: "Best Estimate", dashed: true },
];

export function ForecastTimelineChart({
  data,
  height = 300,
  yAxisLabel = "Customers",
  className,
  showGrid = true,
  showTooltip = true,
  showThreats = true,
  showLegend = true,
}: ForecastTimelineChartProps) {
  const fillsParent =
    typeof height === "string" && height.trim().endsWith("%");
  // Check which data keys exist
  const hasEstimate25th = data.some((d) => d.estimate25th !== undefined);
  const hasMedian = data.some((d) => d.predicted_median !== undefined);
  const hasEstimate75th = data.some((d) => d.estimate75th !== undefined);
  const hasEstimate90th = data.some((d) => d.estimate90th !== undefined);
  const hasBestEstimate = data.some((d) => d.best_estimate !== undefined);

  const visibility: Record<string, boolean> = {
    estimate25th: hasEstimate25th,
    predicted_median: hasMedian,
    estimate75th: hasEstimate75th,
    estimate90th: hasEstimate90th,
    best_estimate: hasBestEstimate,
  };

  const visibleLegendItems = LEGEND_ITEMS.filter((item) => visibility[item.key]);

  return (
    <div
      className={cn(
        "flex w-full flex-col",
        fillsParent && "h-full min-h-0",
        className,
      )}
    >
      {showLegend && visibleLegendItems.length > 0 && (
        <div className="mb-3 flex flex-shrink-0 flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          {visibleLegendItems.map((item) => (
            <div key={item.key} className="flex items-center gap-1.5">
              <span
                className="inline-block shrink-0 w-5"
                style={{
                  height: 2,
                  ...(item.dashed
                    ? {
                        borderTop: `2px dashed ${LINE_COLORS[item.key]}`,
                        backgroundColor: "transparent",
                      }
                    : {
                        backgroundColor: LINE_COLORS[item.key],
                      }),
                }}
              />
              <span className="text-gray-600 dark:text-gray-400">
                {item.label}
              </span>
            </div>
          ))}
        </div>
      )}
    <div
      className={cn("w-full", fillsParent && "min-h-0 flex-1")}
      style={fillsParent ? { minHeight: 0 } : { height }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{ top: 30, right: 20, left: 20, bottom: 20 }}
        >
          {/* Gradients */}
          <defs>
            <linearGradient id="gradient25th" x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor={LINE_COLORS.estimate25th}
                stopOpacity={0.6}
              />
              <stop
                offset="100%"
                stopColor={LINE_COLORS.estimate25th}
                stopOpacity={0.1}
              />
            </linearGradient>
            <linearGradient id="gradientMedian" x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor={LINE_COLORS.predicted_median}
                stopOpacity={0.6}
              />
              <stop
                offset="100%"
                stopColor={LINE_COLORS.predicted_median}
                stopOpacity={0.1}
              />
            </linearGradient>
            <linearGradient id="gradient75th" x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor={LINE_COLORS.estimate75th}
                stopOpacity={0.6}
              />
              <stop
                offset="100%"
                stopColor={LINE_COLORS.estimate75th}
                stopOpacity={0.1}
              />
            </linearGradient>
            <linearGradient id="gradient90th" x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor={LINE_COLORS.estimate90th}
                stopOpacity={0.35}
              />
              <stop
                offset="100%"
                stopColor={LINE_COLORS.estimate90th}
                stopOpacity={0.05}
              />
            </linearGradient>
          </defs>

          {/* Grid */}
          {showGrid && (
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#e5e7eb"
              className="dark:stroke-gray-700"
            />
          )}

          {/* X Axis */}
          <XAxis
            dataKey="date"
            stroke="#6b7280"
            fontSize={12}
            tickLine={{ stroke: "#6b7280" }}
            axisLine={{ stroke: "#6b7280" }}
            tickFormatter={formatDate}
          />

          {/* Y Axis */}
          <YAxis
            stroke="#6b7280"
            fontSize={12}
            tickLine={{ stroke: "#6b7280" }}
            axisLine={{ stroke: "#6b7280" }}
            tickFormatter={formatCompactNumber}
            label={
              yAxisLabel
                ? {
                    value: yAxisLabel,
                    angle: -90,
                    position: "insideLeft",
                    style: { textAnchor: "middle", fill: "#6b7280" },
                  }
                : undefined
            }
          />

          {/* Tooltip */}
          {showTooltip && (
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ stroke: "#9ca3af", strokeWidth: 1 }}
            />
          )}

          {/* Areas - render in order from largest to smallest */}
          {hasEstimate90th && (
            <Area
              type="monotone"
              dataKey="estimate90th"
              name="90th Percentile"
              stroke={LINE_COLORS.estimate90th}
              strokeWidth={2}
              fill="url(#gradient90th)"
              dot={false}
              activeDot={false}
            />
          )}

          {hasEstimate75th && (
            <Area
              type="monotone"
              dataKey="estimate75th"
              name="75th Percentile"
              stroke={LINE_COLORS.estimate75th}
              strokeWidth={2}
              fill="url(#gradient75th)"
              dot={false}
              activeDot={false}
            />
          )}

          {hasMedian && (
            <Area
              type="monotone"
              dataKey="predicted_median"
              name="Median"
              stroke={LINE_COLORS.predicted_median}
              strokeWidth={2}
              fill="url(#gradientMedian)"
              dot={false}
              activeDot={false}
            />
          )}

          {hasEstimate25th && (
            <Area
              type="monotone"
              dataKey="estimate25th"
              name="25th Percentile"
              stroke={LINE_COLORS.estimate25th}
              strokeWidth={2}
              fill="url(#gradient25th)"
              dot={false}
              activeDot={false}
            />
          )}

          {/* Best Estimate - dashed line, no fill */}
          {hasBestEstimate && (
            <Area
              type="monotone"
              dataKey="best_estimate"
              name="Best Estimate"
              stroke={LINE_COLORS.best_estimate}
              strokeWidth={2}
              strokeDasharray="5 5"
              fill="none"
              dot={false}
              activeDot={false}
            />
          )}

          {/* Threat indicators at top */}
          {showThreats &&
            data.map((point, pointIndex) => {
              const activeThreats =
                point.threats?.filter((t) => t.isActive) || [];
              if (activeThreats.length === 0) return null;

              return activeThreats.map((threat, threatIndex) => {
                const Icon = getThreatIcon(threat.slug) || AlertTriangle;
                const color = getThreatColor(threat.slug) || "#6b7280";

                return (
                  <ReferenceLine
                    key={`threat-${point.date}-${threat.slug}`}
                    x={point.date}
                    stroke="transparent"
                    label={{
                      value: "",
                      position: "top",
                      content: (props: any) => {
                        const { viewBox } = props;
                        if (!viewBox) return null;

                        return (
                          <foreignObject
                            x={viewBox.x - 10}
                            y={5 + threatIndex * 22}
                            width="20"
                            height="20"
                          >
                            <div
                              className="flex items-center justify-center w-5 h-5 rounded-full shadow-sm"
                              style={{ backgroundColor: color }}
                              title={threat.name}
                            >
                              <Icon className="h-3 w-3 text-white" />
                            </div>
                          </foreignObject>
                        );
                      },
                    }}
                  />
                );
              });
            })}
        </AreaChart>
      </ResponsiveContainer>
    </div>
    </div>
  );
}

export default ForecastTimelineChart;
